/**
 * One short tick, on the platforms that have one.
 *
 * `navigator.vibrate` is the obvious answer and it is not the answer on the
 * device this app is mostly used on: WebKit has never shipped it, so on an
 * iPhone the call is a no-op and the gesture is silent. What iOS does expose,
 * since 17.4, is the switch control — `<input type="checkbox" switch>` — and
 * toggling one plays the system's own tick through the taptic engine. So a
 * switch nobody can see, toggled from code, is the only way to ask iOS for a
 * haptic from a web page. It is a trick, but it is the platform's own tick
 * rather than an impression of one, and it costs a single 1px node.
 *
 * Everything else gets `navigator.vibrate`. Anything with neither gets
 * nothing, silently: this is a garnish on a gesture that has to work without
 * it, so nothing here is allowed to throw or to block.
 */

/** Below this, a fast scrub across a year turns into a buzz rather than ticks. */
const MIN_INTERVAL_MS = 24;

let lastTickAt = -Infinity;

/**
 * `undefined` = not looked for yet, `null` = looked and this platform has no
 * switch. Built on first use rather than at import: a module that appends to
 * `document.body` as a side effect of being imported breaks anything that
 * loads it before there is a body, tests included.
 */
let switchLabel: HTMLLabelElement | null | undefined;

const now = () =>
  typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();

const supportsSwitch = () =>
  typeof document !== "undefined" &&
  typeof HTMLInputElement !== "undefined" &&
  "switch" in HTMLInputElement.prototype;

const getSwitchLabel = (): HTMLLabelElement | null => {
  if (switchLabel !== undefined) return switchLabel;
  if (!supportsSwitch() || !document.body) {
    switchLabel = null;
    return null;
  }

  const label = document.createElement("label");
  // Out of the way but still rendered. `display: none` would be the tidy way
  // to hide it and it takes the haptic with it — an unrendered control has
  // nothing to toggle.
  label.style.cssText =
    "position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;";
  label.setAttribute("aria-hidden", "true");

  const input = document.createElement("input");
  input.type = "checkbox";
  input.setAttribute("switch", "");
  // Not a stop on the way through the page for anyone using a keyboard.
  input.tabIndex = -1;
  label.appendChild(input);

  document.body.appendChild(label);
  switchLabel = label;
  return label;
};

/**
 * Play a tick. Safe to call as often as a gesture wants to — calls closer
 * together than a couple of frames are dropped.
 */
export function hapticTick(): void {
  const at = now();
  if (at - lastTickAt < MIN_INTERVAL_MS) return;
  lastTickAt = at;

  try {
    const label = getSwitchLabel();
    if (label) {
      // Toggling it in either direction is what plays the tick, so this
      // deliberately doesn't care which way the switch ends up.
      label.click();
      return;
    }
    navigator.vibrate?.(6);
  } catch {
    // A platform that refuses is not worth a broken gesture.
  }
}
